import { addressAndPort } from '../../server/address.ts';
import type { WebSocket } from 'ws';
import getLogger from '../../logger.ts';
import type { Authentication } from '../../server/security/types.ts';
import { IOGateway } from '@interopio/gateway';

import type {ServerWebSocketHandler} from '../../../types/web/server';
import type {AddressInfo} from 'node:net';
import {AsyncLocalStorage} from 'node:async_hooks';

const log = getLogger('ws');
const codec = IOGateway.Encoding.json<IOGateway.Message>();

function principalName(authentication: Authentication): string | undefined {
    let name: string | undefined;
    if (authentication.authenticated) {
        name = authentication.name;
        if (name === undefined) {
            if (authentication['principal'] !== undefined) {
                const principal = authentication['principal'];
                if (typeof principal === 'object') {
                    name = principal.name;
                }
                if (name === undefined) {
                    if (principal === undefined) {
                        name = '';
                    }
                    else {
                        name = String(principal);
                    }
                }
            }
        }
    }
    return name;
}

function initClient(this: IOGateway.Gateway,
                    socket: WebSocket,
                    authenticationPromise: () => Promise<Authentication | undefined>,
                    remoteAddress?: AddressInfo
): IOGateway.GatewayClient<string> | undefined {
    const key = addressAndPort(remoteAddress);
    const host = remoteAddress?.address ?? '<unknown>';
    const opts = {
        key,
        host,
        codec,
        onAuthenticate: async (): Promise<IOGateway.Auth.AuthenticationSuccess> => {
            const authentication = (await authenticationPromise());
            if (authentication?.authenticated) {
                return {type: 'success', user: principalName(authentication)};
            }
            throw new Error(`no valid client authentication ${key}`);
        },
        onPing: () => {
            socket.ping((err?: Error) => {
                if (err) {
                    log.warn(`failed to ping ${key}`, err);
                }
                else {
                    log.info(`ping sent to ${key}`);
                }
            });
        },
        onDisconnect: (reason: 'inactive' | 'shutdown') => {
            switch (reason) {
                case 'inactive': {
                    log.warn(`no heartbeat (ping) received from ${key}, closing socket`);
                    socket.close(4001, 'ping expected');
                    break;
                }
                case 'shutdown': {
                    socket.close(1001, 'shutdown');
                    break;
                }
            }
        }
    };
    try {
        return this.client((data) => socket.send(data), opts as IOGateway.GatewayClientOptions<string>);
    } catch (err) {
        log.warn(`${key} failed to create client`, err);
    }
}

async function create(this: IOGateway.Gateway, environment: {
    endpoint: string,
    storage?: AsyncLocalStorage<{ }>
}): Promise<ServerWebSocketHandler> {
    log.info(`starting gateway on ${environment.endpoint}`);
    await this.start(environment);


    return async ({socket, handshake}) => {
        const {logPrefix, remoteAddress, principal: principalPromise} = handshake;
        log.info(`${logPrefix}connected on gw`);
        const client = initClient.call(this, socket, principalPromise, remoteAddress);
        if (!client) {
            log.error(`${logPrefix}gw client init failed`);
            socket.terminate();
            return;
        }
        socket.on('error', (err: Error) => {
            log.error(`${logPrefix}websocket error: ${err}`, err);
        });

        const contextFn = environment.storage !== undefined ? AsyncLocalStorage.snapshot() : undefined;
        socket.on('message', (data, _isBinary) => {
            if (Array.isArray(data)) {
                data = Buffer.concat(data);
            }
            // JSON.parse will invoke abstract operation ToString (coerce it) prior parsing (https://262.ecma-international.org/5.1/#sec-15.12.2)
            if (contextFn !== undefined) {
                contextFn(() => client.send(data as unknown as string))
            }
            else {
                client.send(data as unknown as string);
            }

        });
        socket.on('close', (code) => {
            log.info(`${logPrefix}disconnected from gw. code: ${code}`);
            client.close();
        });
    }
}

export default create;
